import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  useTheme,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Summaryblackicon from "../../../../assets/Group.png";
import PrintInwardicon from "../../../../assets/printinwardblueicon.png";
import QCrejectionIcon from "../../../../assets/qualityrejection1blueicon.png";
import RawInwardIcon from "../../../../assets/rawinwardblueicon.png";
import QCrejectionIcon2 from "../../../../assets/qualityrejectionblueicon.png";
import PrintOutwardicon from "../../../../assets/prntoutwardicon.png";
import CustomTypography from "../form/CustomTypography";
import { ProjectInventoryOpsSummary } from "../../../../models/ProjectManagement/ProjectInventoryProduct";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import dayjs from "dayjs";

const ENTRY_TYPES = [
  "print_inward",
  "raw_inward",
  "print_inward_qc",
  "raw_inward_qc",
  "print_outward",
  "raw_inward_qc_image",
  "print_inward_qc_image"
];

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

// Helper functions
const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-GB").replace(/\//g, "-");

const getIconForType = (type: string) => {
  const icons = {
    print_inward_qc: QCrejectionIcon2,
    print_inward: PrintInwardicon,
    raw_inward_qc: QCrejectionIcon,
    raw_inward: RawInwardIcon,
    print_outward: PrintOutwardicon,
  };
  return (
    <img
      src={icons[type as keyof typeof icons] || RawInwardIcon}
      alt={type}
      width={40}
      height={40}
    />
  );
};

const getLabelForType = (type: string) => {
  const labels = {
    print_inward: "Print Inward",
    raw_inward: "Raw Inward",
    print_inward_qc: "2nd Quality Rejection",
    raw_inward_qc: "1st Quality Rejection",
    print_outward: "Print Outward",
    raw_inward_qc_image: "1st QC Image",
    print_inward_qc_image: "2nd QC Image"
  };
  return labels[type as keyof typeof labels] || "";
};

// Reusable component for detail items
const DetailItem: React.FC<{
  theme: Theme;
  label: string;
  value: React.ReactNode;
  image?: string;
}> = ({ theme, label, value, image}) => (
  <CustomTypography
    variant="body2"
    style={{
      border: `1px solid ${theme.palette.customColors.greyTextColor}`,
      display: "inline-block",
      borderRadius: "4px",
      fontWeight: 600,
      padding: 7,
      marginTop: 10,

      color: theme.palette.customColors.greyTextColor,
    }}
  >
    {label && (
    <>
      • {label}:{" "}
    </>
    )}
    <span
      style={{
        color: theme.palette.customColors.blackBorder,
        fontWeight: 600,
      }}
    >
      {value}
      {image && (
      <>
        {" "}
        <a href={image} target="_blank" rel="noopener noreferrer">
          View Image
        </a>
      </>
    )}
    </span>
  </CustomTypography>
);

interface SummaryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  summaryData: ProjectInventoryOpsSummary[];
}

const SummaryDrawer: React.FC<SummaryDrawerProps> = observer(
  ({ isOpen, onClose, summaryData }) => {
    const theme = useTheme<Theme>();
    const { projectInventoryOpsStore } = useStores();
    const currentPoId = projectInventoryOpsStore.currentPoDetails?.po_id;

    const filteredData = summaryData.filter(
      (summary) => summary.po_id === currentPoId
    );

    return (
      <Drawer
        anchor="right"
        open={isOpen}
        sx={{
          "& .MuiDrawer-paper": {
            height: "80vh",
            borderRadius: "10px 0 0 10px",
            boxShadow: 3,
            display: "flex",
            flexDirection: "column",
            mt: "13vh",
          },
        }}
      >
        <Box
          sx={{
            pt: 2,
            pr: 3,
            pb: 0,
            pl: 3,
            zIndex: 1,
            backgroundColor: "background.paper",
            width: {
              xl: "30vw",
              lg: "40vw",
              md: "50vw",
              sm: "60vw",
              xs: "70vw",
              "@media (max-width:420px)": { width: "85vw" },
            },
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 5, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <CustomTypography
            variant="heading4"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginTop: 15,
            }}
          >
            <img src={Summaryblackicon} alt="" width={25} />
            Summary
          </CustomTypography>
          <Divider sx={{ mt: 2 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 3,
            pt: 0,
            width: {
              xxl: "25vw",
              xl: "30vw",
              lg: "40vw",
              md: "50vw",
              sm: "60vw",
              xs: "70vw",
            },
            "@media (max-width:420px)": { width: "85vw" },
          }}
        >
          {filteredData.length > 0 &&
          filteredData.some(
            (summaryItem) =>
              summaryItem.history && Object.keys(summaryItem.history).length > 0
          ) ? (
            filteredData.flatMap((summaryItem, index) =>
              Object.entries(summaryItem.history || {}).map(
                ([date, entries]) => (
                  <Box key={`${date}-${index}`}>
                    <CustomTypography
                      variant="heading7"
                      fontWeight={600}
                      color={theme.palette.customColors.blackBorder}
                      style={{ margin: "15px 0" }}
                    >
                      {dayjs(date).format("DD-MM-YYYY")}
                    </CustomTypography>

                    {entries
                      .filter((entry) => ENTRY_TYPES.includes(entry.type))
                      .map((entry, entryIndex) => {
                        const timeString = new Date(
                          entry.modified_date ?? ""
                        ).toLocaleTimeString("en-US", DATE_OPTIONS);

                        return (
                          <Box
                            key={entry.id || entryIndex}
                            sx={{ display: "flex", gap: 2 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                minWidth: 40,
                                flexDirection: "column",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                {getIconForType(entry.type)}
                              </Box>
                              {entryIndex < entries.length - 1 && (
                                <Box
                                  sx={{
                                    width: "2px",
                                    height: "100%",
                                    backgroundColor: theme.palette.divider,
                                  }}
                                />
                              )}
                            </Box>

                            <Box sx={{ flex: 1, marginBottom: 2 }}>
                              <CustomTypography
                                variant="body3"
                                fontWeight={600}
                                color={theme.palette.customColors.greyTextColor}
                              >
                                {timeString}
                              </CustomTypography>
                              <CustomTypography
                                variant="heading8"
                                style={{ marginTop: 2 }}
                              >
                                {entry.admin_name}{" "}
                                <Box
                                  component="span"
                                  sx={{
                                    color:
                                      theme.palette.customColors.greyTextColor,
                                  }}
                                >
                                  Updated
                                </Box>{" "}
                                {getLabelForType(entry.type)}
                              </CustomTypography>

                              {entry.type === "print_outward" && (
                                <>
                                  <DetailItem
                                    theme={theme}
                                    label="Printer"
                                    value={entry.printer_name}
                                  />
                                  <br />
                                  <DetailItem
                                    theme={theme}
                                    label="Qty Sent"
                                    value={entry.quantity_sent}
                                  />
                                  <br />
                                  {entry.timeline && (
                                    <DetailItem
                                      theme={theme}
                                      label="Timeline"
                                      value={formatDate(entry.timeline)}
                                    />
                                  )}
                                </>
                              )}

                              {["print_inward", "raw_inward"].includes(
                                entry.type
                              ) && (
                                <DetailItem
                                  theme={theme}
                                  label="Quantity Received"
                                  value={entry.quantity_received}
                                />
                              )}

                              {["print_inward_qc", "raw_inward_qc"].includes(
                                entry.type
                              ) && (
                                <DetailItem
                                  theme={theme}
                                  label="Rejected Quantity"
                                  value={entry.rejected_quantity}
                                />
                              )}

                              {["print_inward_qc_image", "raw_inward_qc_image"].includes(
                                entry.type
                              ) && (
                                <DetailItem
                                  theme={theme}
                                  label={""}
                                  value={entry.rejected_quantity}
                                  image={entry.image_url}
                                />
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                  </Box>
                )
              )
            )
          ) : (
            <CustomTypography
              variant="body1"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginTop: "20px", textAlign: "center" }}
            >
              No summary found
            </CustomTypography>
          )}
        </Box>
      </Drawer>
    );
  }
);

export default SummaryDrawer;
